import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { DepositAddressService } from "@/services/DepositAddress";
import { NetworkCurrencyService } from "@/services/NetworkCurrency";
import { DepositModalComponent } from "./DepositModalComponent";

interface DepositModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	shortfall?: number;
}

export const DepositModal = ({ open, onOpenChange, shortfall }: DepositModalProps) => {
	const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
	const [copied, setCopied] = useState(false);
	const isMobile = useIsMobile();

	const { data: currencies = [], isLoading: isLoadingCurrencies } =
		NetworkCurrencyService.useGetNetworkCurrenciesQuery(undefined, { skip: !open });

	const { data: addresses = [], isLoading: isLoadingAddresses } =
		DepositAddressService.useGetAddressesQuery(undefined, { skip: !open });

	const [generateAddress, { isLoading: isGenerating }] =
		DepositAddressService.useGenerateAddressMutation();

	const selectedCurrency = useMemo(
		() => currencies.find((c) => c.id === selectedCurrencyId) ?? null,
		[currencies, selectedCurrencyId],
	);

	const addressForCurrency = useMemo(
		() =>
			!selectedCurrencyId
				? null
				: addresses.find((a) => a.networkCurrencyId === selectedCurrencyId) ?? null,
		[addresses, selectedCurrencyId],
	);

	const address = addressForCurrency?.address ?? "";
	const needsGeneration = !!selectedCurrencyId && !addressForCurrency && !isLoadingAddresses;

	const handleGenerate = useCallback(async () => {
		if (!selectedCurrencyId) return;
		try {
			await generateAddress({ networkCurrencyId: selectedCurrencyId }).unwrap();
			toast.success("Deposit address generated");
		} catch (err: any) {
			const message = err?.data?.detail ?? "Failed to generate address";
			toast.error(message);
		}
	}, [selectedCurrencyId, generateAddress]);

	const handleCopy = useCallback(async () => {
		if (!address) return;
		const ok = await copyToClipboard(address);
		if (ok) {
			setCopied(true);
			toast.success("Address copied");
			setTimeout(() => setCopied(false), 2000);
		} else {
			toast.error("Failed to copy. Please select and copy manually.");
		}
	}, [address]);

	const handleOpenChange = useCallback(
		(next: boolean) => {
			if (!next) {
				setSelectedCurrencyId("");
				setCopied(false);
			}
			onOpenChange(next);
		},
		[onOpenChange],
	);

	const currencyOptions = useMemo(
		() =>
			currencies.map((c) => ({
				value: c.id,
				label: `${c.currencySymbol} — ${c.networkName}`,
			})),
		[currencies],
	);

	return (
		<DepositModalComponent
			open={open}
			onOpenChange={handleOpenChange}
			isMobile={isMobile}
			selectedCurrencyId={selectedCurrencyId}
			onCurrencyChange={setSelectedCurrencyId}
			currencyOptions={currencyOptions}
			isLoadingCurrencies={isLoadingCurrencies}
			address={address}
			copied={copied}
			onCopy={handleCopy}
			needsGeneration={needsGeneration}
			isGenerating={isGenerating}
			onGenerate={handleGenerate}
			currencySymbol={selectedCurrency?.currencySymbol}
			networkName={selectedCurrency?.networkName}
			shortfall={shortfall}
		/>
	);
};
